import useTranslator from "../../hooks/useTranslator";
import { useMemo, useState } from "react";
import { toast } from "../../helper/common";
import {
  EXPLAINED_DOC_MAX_LENGTH,
  getExplainedDocCanonicalKeys,
  canonicalizeMissingDocKey,
  isCanonicalKeyCovered,
} from "../../Utils/missingDocNote";
import MissingDocumentsExplanationBlock from "../Onboarding/MissingDocumentsExplanationBlock";
import styles from "./MissingDocumentsNoteModal.module.css";

// Modal with the same explanation block as the sign-up documents step.
// It edits the tenant's single explanation note (TenantMissingDocNote):
// saving replaces the note, unselecting everything and saving deletes it.
// Props:
// - missingDocuments: keys of the documents Skor did not detect (the
//   checkbox list), plus documents uploaded for another person (third-party
//   docs do not count for this tenant, so they are explainable too).
//   Explained documents that were detected later are no longer missing, so
//   they drop out of the list automatically.
// - initialNote: the tenant's current note, or null.
// - onSave: persists { explainedDocuments, explanationMessage }, resolves
//   with the saved note (or null when deleted).
// - onClose: closes the modal without saving.
// - translateDocName: maps a document key to its display label.
const MissingDocumentsNoteModal = ({
  missingDocuments = [],
  initialNote = null,
  onSave,
  onClose,
  translateDocName,
}) => {
  const { _t } = useTranslator();
  const normalizedMissingDocuments = useMemo(
    () => (Array.isArray(missingDocuments) ? missingDocuments : []),
    [missingDocuments]
  );
  const [selectedExplainedKeys, setSelectedExplainedKeys] = useState(() => {
    const explainedKeys = getExplainedDocCanonicalKeys(initialNote);
    return normalizedMissingDocuments.filter((docKey) =>
      isCanonicalKeyCovered(explainedKeys, docKey)
    );
  });
  const [explanationMessage, setExplanationMessage] = useState(
    () => initialNote?.explanationMessage ?? ""
  );
  const [savingNote, setSavingNote] = useState(false);

  // This modal remounts on open. Keep its draft when the parent refreshes.

  // Toggle one missing document in the explanation selection. Selection is
  // canonical-key based (one entry per document slot) so sign-up and profile keys that
  // describe the same document never duplicate each other.
  const toggleExplainedDocumentKey = (docKey) => {
    const toggledCanonicalKey = canonicalizeMissingDocKey(docKey);
    setSelectedExplainedKeys((currentSelectedKeys) => {
      const isCurrentlySelected = currentSelectedKeys.some(
        (selectedKey) =>
          canonicalizeMissingDocKey(selectedKey) === toggledCanonicalKey
      );
      if (isCurrentlySelected) {
        return currentSelectedKeys.filter(
          (selectedKey) =>
            canonicalizeMissingDocKey(selectedKey) !== toggledCanonicalKey
        );
      }
      return [...currentSelectedKeys, docKey];
    });
  };

  // Save the note (or delete it when nothing is selected). A selection
  // without a comment is blocked with a toast, like in the sign-up step.
  const handleSaveNote = async () => {
    const trimmedExplanation = explanationMessage.trim();
    if (selectedExplainedKeys.length > 0 && !trimmedExplanation) {
      toast.error(
        _t(
          "missingDocExplainRequired",
          "Add a short explanation for the landlord."
        )
      );
      return;
    }
    if (typeof onSave !== "function") {
      onClose?.();
      return;
    }
    setSavingNote(true);
    try {
      await onSave({
        explainedDocuments: selectedExplainedKeys
          .filter((docKey) => normalizedMissingDocuments.includes(docKey))
          .map((docKey) => ({
            documentKey: docKey,
            documentLabel: docKey,
          })),
        explanationMessage: trimmedExplanation,
      });
    } finally {
      setSavingNote(false);
    }
  };

  return (
    <div
      className="popup-overlay wrapper-modal modal-fixed-screen"
      onClick={onClose}
    >
      <div
        className={styles.modalCard}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={_t(
          "missingDocExplainDialogTitle",
          "Explain missing documents"
        )}
      >
        <div className={styles.modalHeader}>
          <h2>
            {_t(
              "missingDocumentsSectionTitle",
              "Missing documents section title in onboarding",
              "translation",
              { count: normalizedMissingDocuments.length }
            )}
          </h2>
          <button
            type="button"
            className={styles.modalClose}
            onClick={onClose}
            aria-label={_t("close")}
          >
            ×
          </button>
        </div>
        <MissingDocumentsExplanationBlock
          missingDocuments={normalizedMissingDocuments}
          selectedExplainedKeys={selectedExplainedKeys}
          onToggleExplainedKey={toggleExplainedDocumentKey}
          explanationMessage={explanationMessage}
          onExplanationMessageChange={setExplanationMessage}
          translateDocName={translateDocName}
          idPrefix="profile-missing-documents"
        />
        <div className={styles.modalFooter}>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={onClose}
            disabled={savingNote}
          >
            {_t("cancel")}
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={handleSaveNote}
            disabled={savingNote}
          >
            {savingNote ? `${_t("saving", "Saving")}...` : _t("save")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MissingDocumentsNoteModal;
export { EXPLAINED_DOC_MAX_LENGTH };
